import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  isSignal,
  model,
  output,
  Signal,
} from "@angular/core";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { MatIcon } from "@angular/material/icon";
import { DialogsService } from "../../services/dialogs/dialogs.service";
import { ConfirmationDialogComponent } from "../confirmation-dialog/confirmation-dialog.component";
import { firstValueFrom } from "rxjs";
import { FormArray, FormControl, FormGroup } from "@angular/forms";
import { FormsHelperService } from "../../services/forms-helper.service";
import { SnackService } from "../../services/snack-service.service";
import { TranslateUtils } from "../../utils/translate.utils";

@Component({
  selector: "vl-big-button",
  templateUrl: "./big-button.component.html",
  styleUrls: ["./big-button.component.scss"],
  standalone: true,
  imports: [MatProgressSpinner, MatIcon],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Le clic est écouté sur l'HÔTE, pas sur le div interne : un clic visant
  // l'élément <vl-big-button> (test, script, outil d'accessibilité) tombait
  // sinon dans le vide, sans erreur ni effet.
  host: {
    "(click)": "onHostClick($event)",
    "[attr.role]": "'button'",
    "[attr.aria-disabled]": "_disabled() ? 'true' : null",
    "[attr.aria-busy]": "loading() ? 'true' : null",
  },
})
export class BigButtonComponent {
  text = input<string>();
  icon = input<string>();
  iconRight = input<string>();
  loading = input(false);
  disabled = input(false);
  disabledError = input<string>();
  disabledValues = input<
    (FormGroup | FormArray | boolean | Signal<boolean> | FormControl)[]
  >([]);
  disabledShowError = model<boolean>(false);
  /** `stacked` (2026-08-05) : texte SOUS l'icône, compact — pour les
   *  rangées d'actions en colonne étroite (ex. titre Zone de préparation). */
  type = input<"large" | "fit" | "rounded" | "stacked">("large");
  color = input<
    "primary" | "basic" | "warn" | "accent" | "secondary" | "canvas" | "error"
  >("accent");
  confirmationType = input<"remove" | "custom" | "none">("none");
  confirmationTitle = input<string>();
  confirmationMessage = input<string>();

  action = output();

  dialogService = inject(DialogsService);
  snackService = inject(SnackService);

  _disabled = computed(() => this.disabled() || this.loading());
  colorClasses = computed(() => {
    return `big-button ${this._disabled() ? "big-button-disabled" : "big-button-enabled"} big-button-${this.color()} big-button-${this.type()}`;
  });

  confirmationTypes = {
    remove: {
      title: "Suppression",
      message:
        "Vous êtes sur le point de supprimer cet élément. Confirmez-vous ?",
    },
  };

  onHostClick($event: MouseEvent) {
    // Le bouton reste un point d'arrêt pour le clic, même désactivé : sans ça,
    // cliquer un bouton éteint déclencherait la carte qui le porte.
    $event.stopPropagation();
    void this.onAction($event);
  }

  async onAction($event: MouseEvent) {
    $event.stopPropagation();
    if (this._disabled()) {
      // Un bouton éteint ne doit pas se contenter de ne rien faire : quand on
      // sait pourquoi il l'est, on le dit.
      if (!this.loading() && this.disabledError()) {
        this.snackService.warn(this.disabledError()!);
      }
      return;
    }
    // Les valeurs manquantes se disent AVANT la confirmation : demander
    // « confirmez-vous ? » pour ensuite ne rien faire n'apprend rien.
    if (this.testDisableValues()) {
      return;
    }
    if (this.confirmationType() !== "none") {
      const text = this.buildConfirmationType();
      const dialogRef = this.dialogService.open(
        ConfirmationDialogComponent,
        "fit-content",
        "fit-content",
        {
          title: text.title,
          message: text.message,
        },
      );
      const result = await firstValueFrom(dialogRef.afterClosed());
      if (!result) {
        return;
      }
    }
    this.action.emit();
  }

  buildConfirmationType() {
    if (this.confirmationType() === "custom") {
      return {
        title: this.confirmationTitle(),
        message: this.confirmationMessage(),
      };
    }
    return this.confirmationTypes[this.confirmationType()];
  }

  testDisableValues(): boolean {
    let hasError = false;
    for (const value of this.disabledValues() ?? []) {
      if (typeof value === "boolean") {
        if (value) {
          hasError = true;
        }
      } else if (isSignal(value)) {
        if (value()) {
          hasError = true;
        }
      } else if (FormsHelperService.isAbstractControl(value)) {
        if (value.invalid) {
          if (value instanceof FormGroup) {
            FormsHelperService.markGroupDirty(value);
          } else if (value instanceof FormArray) {
            FormsHelperService.markArrayDirty(value);
          } else {
            FormsHelperService.markControlDirty(value);
          }
          value.updateValueAndValidity();
          hasError = true;
        }
      }
    }
    if (hasError) {
      const error =
        this.disabledError() ??
        TranslateUtils.translate("button.invalid.default");
      this.snackService.warn(error);
      this.disabledShowError.set(true);
    } else {
      this.disabledShowError.set(false);
    }
    return hasError;
  }
}
